import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { useToast } from '../../../hooks/use-toast';
import { Loader2, Save, Plus, Trash2, Image as ImageIcon, Upload } from 'lucide-react';
import BackButton from '../../../components/admin/BackButton';
import ImageSuggestionModal from '../../../components/admin/ImageSuggestionModal';
import BunnyLibraryModal from '../../../components/admin/BunnyLibraryModal';
import TranslateButton from '../../../components/admin/TranslateButton';
import AutoTranslateIndicator from '../../../components/admin/AutoTranslateIndicator';
import { uploadImage } from '../../../services/imageService';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HighlightForm = () => {
  const { type } = useParams(); // 'trading' or 'procurement'
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const sectionName = `highlight_${type}`;
  const displayName = type === 'trading' ? 'Trading Global' : 'Procurement Estratégico';
  
  const [formData, setFormData] = useState({
    enabled: true,
    badge_pt: '',
    badge_en: '',
    title_pt: '',
    title_en: '',
    subtitle_pt: '',
    subtitle_en: '',
    description_pt: '',
    description_en: '',
    pillars: [
      { icon: '', title_pt: '', title_en: '', description_pt: '', description_en: '' },
      { icon: '', title_pt: '', title_en: '', description_pt: '', description_en: '' },
      { icon: '', title_pt: '', title_en: '', description_pt: '', description_en: '' }
    ],
    cta_primary: { text_pt: '', text_en: '', link: '' },
    cta_secondary: { text_pt: '', text_en: '', link: '' },
    trust_note_pt: '',
    trust_note_en: '',
    background_image: '',
    overlay_opacity: 40,
    image_brightness: 100
  });

  useEffect(() => {
    loadHighlightData();
  }, [type]);

  const loadHighlightData = async () => {
    setLoading(true);
    try {
      // Load RAW (no language filter) so we always see the actual _pt / _en
      // structure of the document — critical for multi-language pillar edits.
      const response = await axios.get(`${API_URL}/api/homepage-ml/${sectionName}?lang=raw`);
      const raw = response.data || {};

      setFormData({
        enabled: raw.enabled !== undefined ? raw.enabled : true,
        badge_pt: raw.badge_pt || '',
        badge_en: raw.badge_en || '',
        title_pt: raw.title_pt || '',
        title_en: raw.title_en || '',
        subtitle_pt: raw.subtitle_pt || '',
        subtitle_en: raw.subtitle_en || '',
        description_pt: raw.description_pt || '',
        description_en: raw.description_en || '',
        pillars: (raw.pillars && raw.pillars.length ? raw.pillars : [
          { icon: '', title_pt: '', title_en: '', description_pt: '', description_en: '' },
          { icon: '', title_pt: '', title_en: '', description_pt: '', description_en: '' },
          { icon: '', title_pt: '', title_en: '', description_pt: '', description_en: '' }
        ]).map((p) => ({
          icon: p.icon || '',
          title_pt: p.title_pt || '',
          title_en: p.title_en || '',
          description_pt: p.description_pt || '',
          description_en: p.description_en || '',
        })),
        cta_primary: raw.cta_primary
          ? {
              text_pt: raw.cta_primary.text_pt || '',
              text_en: raw.cta_primary.text_en || '',
              link: raw.cta_primary.link || '',
            }
          : { text_pt: '', text_en: '', link: '' },
        cta_secondary: raw.cta_secondary
          ? {
              text_pt: raw.cta_secondary.text_pt || '',
              text_en: raw.cta_secondary.text_en || '',
              link: raw.cta_secondary.link || '',
            }
          : { text_pt: '', text_en: '', link: '' },
        trust_note_pt: raw.trust_note_pt || '',
        trust_note_en: raw.trust_note_en || '',
        background_image: raw.background_image || '',
        overlay_opacity: raw.overlay_opacity != null ? raw.overlay_opacity : 40,
        image_brightness: raw.image_brightness != null ? raw.image_brightness : 100
      });
    } catch (error) {
      console.error('Failed to load highlight data:', error);
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePillarChange = (index, field, value) => {
    setFormData(prev => {
      const newPillars = [...prev.pillars];
      newPillars[index] = { ...newPillars[index], [field]: value };
      return { ...prev, pillars: newPillars };
    });
  };

  const handleCTAChange = (ctaType, field, value) => {
    setFormData(prev => ({
      ...prev,
      [ctaType]: { ...prev[ctaType], [field]: value }
    }));
  };

  const handleImageSelect = (url) => {
    setFormData(prev => ({ ...prev, background_image: url }));
    toast({ title: "Imagem selecionada" });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({ variant: "destructive", title: "Erro", description: "Selecione uma imagem válida" });
      return;
    }

    setUploading(true);
    try {
      const result = await uploadImage(file, `images/highlights/${type}`);
      if (result.success) {
        setFormData(prev => ({ ...prev, background_image: result.data.cdn_url }));
        toast({ title: "Upload completo" });
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Erro no upload" });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API_URL}/api/homepage-ml/${sectionName}`, formData);
      toast({ title: "Sucesso", description: `Secção ${displayName} guardada` });
    } catch (error) {
      toast({ variant: "destructive", title: "Erro", description: "Falha ao guardar" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#00B4D8]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <BackButton />

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Highlight: {displayName}</CardTitle>
            <CardDescription>Configure a secção destacada de {displayName} (PT/EN)</CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Toggle Enabled */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-semibold">Activar Secção</h3>
                  <p className="text-sm text-gray-600">Mostrar esta secção na homepage</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => handleInputChange('enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00B4D8]"></div>
                </label>
              </div>

              {/* Portuguese */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4 text-[#00B4D8]">🇵🇹 Português</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Badge (PT)</label>
                    <input
                      type="text"
                      value={formData.badge_pt}
                      onChange={(e) => handleInputChange('badge_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="TRADING GLOBAL"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Título (PT)</label>
                    <input
                      type="text"
                      value={formData.title_pt}
                      onChange={(e) => handleInputChange('title_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Subtítulo (PT)</label>
                    <input
                      type="text"
                      value={formData.subtitle_pt}
                      onChange={(e) => handleInputChange('subtitle_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Descrição (PT)</label>
                    <textarea
                      value={formData.description_pt}
                      onChange={(e) => handleInputChange('description_pt', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Nota de Confiança (PT)</label>
                    <input
                      type="text"
                      value={formData.trust_note_pt}
                      onChange={(e) => handleInputChange('trust_note_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="Gerimos mais de €80M..."
                    />
                  </div>
                </div>
              </div>

              {/* English */}
              <div className="border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#00B4D8]">🇬🇧 English</h3>
                  <TranslateButton
                    sourceTexts={{
                      badge: formData.badge_pt,
                      title: formData.title_pt,
                      subtitle: formData.subtitle_pt,
                      description: formData.description_pt,
                      trust_note: formData.trust_note_pt
                    }}
                    onTranslated={(translated) => {
                      setFormData(prev => ({
                        ...prev,
                        badge_en: translated.badge || '',
                        title_en: translated.title || '',
                        subtitle_en: translated.subtitle || '',
                        description_en: translated.description || '',
                        trust_note_en: translated.trust_note || ''
                      }));
                    }}
                  />
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Badge (EN)</label>
                    <input
                      type="text"
                      value={formData.badge_en}
                      onChange={(e) => handleInputChange('badge_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="GLOBAL TRADING"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Title (EN)</label>
                    <input
                      type="text"
                      value={formData.title_en}
                      onChange={(e) => handleInputChange('title_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Subtitle (EN)</label>
                    <input
                      type="text"
                      value={formData.subtitle_en}
                      onChange={(e) => handleInputChange('subtitle_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Description (EN)</label>
                    <textarea
                      value={formData.description_en}
                      onChange={(e) => handleInputChange('description_en', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Trust Note (EN)</label>
                    <input
                      type="text"
                      value={formData.trust_note_en}
                      onChange={(e) => handleInputChange('trust_note_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="We manage over €80M..."
                    />
                  </div>
                </div>
              </div>

              {/* Pillars (3) */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Pilares (3)</h3>
                <div className="space-y-4">
                  {formData.pillars.map((pillar, idx) => (
                    <Card key={idx} className="p-4">
                      <h4 className="font-medium mb-3">Pilar {idx + 1}</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={pillar.icon}
                          onChange={(e) => handlePillarChange(idx, 'icon', e.target.value)}
                          placeholder="Ícone (ex: TrendingUp)"
                          className="px-3 py-2 border rounded col-span-2"
                        />
                        <input
                          type="text"
                          value={pillar.title_pt}
                          onChange={(e) => handlePillarChange(idx, 'title_pt', e.target.value)}
                          placeholder="Título PT"
                          className="px-3 py-2 border rounded"
                        />
                        <input
                          type="text"
                          value={pillar.title_en}
                          onChange={(e) => handlePillarChange(idx, 'title_en', e.target.value)}
                          placeholder="Title EN"
                          className="px-3 py-2 border rounded"
                        />
                        <input
                          type="text"
                          value={pillar.description_pt}
                          onChange={(e) => handlePillarChange(idx, 'description_pt', e.target.value)}
                          placeholder="Descrição PT"
                          className="px-3 py-2 border rounded"
                        />
                        <input
                          type="text"
                          value={pillar.description_en}
                          onChange={(e) => handlePillarChange(idx, 'description_en', e.target.value)}
                          placeholder="Description EN"
                          className="px-3 py-2 border rounded"
                        />
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* CTAs */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Botões (CTAs)</h3>
                
                <div className="space-y-4">
                  <div className="border p-4 rounded-lg">
                    <h4 className="font-medium mb-3">Botão Principal</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={formData.cta_primary.text_pt}
                        onChange={(e) => handleCTAChange('cta_primary', 'text_pt', e.target.value)}
                        placeholder="Texto PT"
                        className="px-3 py-2 border rounded"
                      />
                      <input
                        type="text"
                        value={formData.cta_primary.text_en}
                        onChange={(e) => handleCTAChange('cta_primary', 'text_en', e.target.value)}
                        placeholder="Text EN"
                        className="px-3 py-2 border rounded"
                      />
                      <input
                        type="text"
                        value={formData.cta_primary.link}
                        onChange={(e) => handleCTAChange('cta_primary', 'link', e.target.value)}
                        placeholder="Link"
                        className="px-3 py-2 border rounded"
                      />
                    </div>
                  </div>

                  <div className="border p-4 rounded-lg">
                    <h4 className="font-medium mb-3">Botão Secundário</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={formData.cta_secondary.text_pt}
                        onChange={(e) => handleCTAChange('cta_secondary', 'text_pt', e.target.value)}
                        placeholder="Texto PT"
                        className="px-3 py-2 border rounded"
                      />
                      <input
                        type="text"
                        value={formData.cta_secondary.text_en}
                        onChange={(e) => handleCTAChange('cta_secondary', 'text_en', e.target.value)}
                        placeholder="Text EN"
                        className="px-3 py-2 border rounded"
                      />
                      <input
                        type="text"
                        value={formData.cta_secondary.link}
                        onChange={(e) => handleCTAChange('cta_secondary', 'link', e.target.value)}
                        placeholder="Link"
                        className="px-3 py-2 border rounded"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Background Image */}
              <div>
                <label className="block text-sm font-medium mb-3">Imagem de Fundo</label>
                
                {formData.background_image && (
                  <div className="relative mb-4">
                    <img src={formData.background_image} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, background_image: '' }))}
                      className="absolute top-2 right-2"
                    >
                      Remover
                    </Button>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <input type="file" id={`${type}-upload`} accept="image/*" onChange={handleFileUpload} className="hidden" />
                    <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => document.getElementById(`${type}-upload`).click()} disabled={uploading}>
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? 'A enviar...' : 'Upload'}
                    </Button>
                  </div>
                  <Button type="button" variant="outline" size="sm" className="w-full border-[#00B4D8] text-[#00B4D8] hover:bg-[#E0F2FE]" onClick={() => setLibraryOpen(true)}>
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Biblioteca
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="w-full border-[#00B4D8] text-[#00B4D8]" onClick={() => setSuggestionsOpen(true)}>
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Sugestões
                  </Button>
                </div>

                {/* Image Controls - Overlay & Brightness */}
                {formData.background_image && (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200 mt-4">
                    <h4 className="text-sm font-bold text-[#2B2D42]">Image Controls</h4>
                    <div className="relative h-28 rounded-lg overflow-hidden">
                      <div className="absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: `url(${formData.background_image})`, filter: `brightness(${(formData.image_brightness || 100) / 100})` }} />
                      <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${(formData.overlay_opacity || 40) / 100})` }} />
                      <div className="absolute inset-0 flex items-center justify-center z-10">
                        <span className="text-white font-bold drop-shadow-lg">Preview</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <label className="text-sm font-medium">Overlay Darkness: {formData.overlay_opacity}%</label>
                        <span className="text-xs text-[#6C757D]">0% = no overlay</span>
                      </div>
                      <input type="range" min="0" max="80" step="5" value={formData.overlay_opacity}
                        onChange={(e) => handleInputChange('overlay_opacity', parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#00B4D8]" />
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <label className="text-sm font-medium">Image Brightness: {formData.image_brightness}%</label>
                        <span className="text-xs text-[#6C757D]">100% = normal</span>
                      </div>
                      <input type="range" min="50" max="150" step="5" value={formData.image_brightness}
                        onChange={(e) => handleInputChange('image_brightness', parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#FF6B35]" />
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="space-y-4 pt-6 border-t">
                <AutoTranslateIndicator />
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => navigate('/admin/homepage')}>Cancelar</Button>
                  <Button type="submit" disabled={saving} className="bg-[#00B4D8]">
                    {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A guardar...</> : <><Save className="w-4 h-4 mr-2" />Guardar</>}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <ImageSuggestionModal
        category="highlights"
        isOpen={suggestionsOpen}
        onClose={() => setSuggestionsOpen(false)}
        onSelect={handleImageSelect}
      />

      <BunnyLibraryModal
        folder={`highlights/${type}`}
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={handleImageSelect}
      />
    </div>
  );
};

export default HighlightForm;
